import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CreditCard,
  CalendarDays,
  AlertCircle,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Receipt,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import api from "../../lib/api";

const STATUS_STYLE = {
  Active: { bg: "bg-green-500/10", text: "text-green-500", icon: CheckCircle2 },
  Expired: { bg: "bg-red-500/10", text: "text-red-500", icon: XCircle },
  Partial: { bg: "bg-amber-500/10", text: "text-amber-500", icon: Clock },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || {
    bg: "bg-secondary",
    text: "text-muted-foreground",
    icon: Clock,
  };
  const Icon = s.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${s.bg} ${s.text}`}
    >
      <Icon className="h-3 w-3" /> {status}
    </span>
  );
}

function ProgressBar({ progress }) {
  return (
    <div className="h-2 bg-secondary rounded-full overflow-hidden">
      <div
        className="h-full bg-primary rounded-full transition-all duration-500"
        style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }}
      />
    </div>
  );
}

function MembershipCard({ ms }) {
  const [open, setOpen] = useState(false);
  const isActive = ["Active", "Partial"].includes(ms.status);

  return (
    <Card
      className={`bg-card border-border ${isActive ? "ring-1 ring-primary/30" : ""}`}
    >
      <CardContent className="p-5 space-y-4">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-semibold text-base">{ms.packageName}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {ms.branchName || ""}
            </p>
          </div>
          <StatusBadge status={ms.status} />
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-secondary rounded-lg px-3 py-2">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-0.5">
              Start
            </p>
            <p className="text-sm font-medium">
              {ms.startDate
                ? new Date(ms.startDate).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                : "—"}
            </p>
          </div>
          <div className="bg-secondary rounded-lg px-3 py-2">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-0.5">
              Expiry
            </p>
            <p className="text-sm font-medium">
              {ms.endDate
                ? new Date(ms.endDate).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                : "—"}
            </p>
          </div>
        </div>

        {/* Progress bar (only for active) */}
        {isActive && typeof ms.daysProgress === "number" && (
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{ms.daysRemaining} days remaining</span>
              <span>{ms.daysProgress}% used</span>
            </div>
            <ProgressBar progress={ms.daysProgress} />
          </div>
        )}

        {/* Fee summary */}
        <div className="grid grid-cols-3 gap-2 pt-1">
          <div className="text-center">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
              Total Fee
            </p>
            <p className="text-sm font-bold">
              ₹{(ms.totalFee || 0).toLocaleString("en-IN")}
            </p>
          </div>
          <div className="text-center">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
              Paid
            </p>
            <p className="text-sm font-bold text-green-500">
              ₹{(ms.paidAmount || 0).toLocaleString("en-IN")}
            </p>
          </div>
          <div className="text-center">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
              Balance
            </p>
            <p
              className={`text-sm font-bold ${ms.balanceDue > 0 ? "text-amber-500" : "text-muted-foreground"}`}
            >
              ₹{(ms.balanceDue || 0).toLocaleString("en-IN")}
            </p>
          </div>
        </div>

        {/* Balance alert */}
        {ms.balanceDue > 0 && (
          <div className="flex items-center gap-2 text-sm text-amber-500 bg-amber-500/10 rounded-lg px-3 py-2">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Balance due: ₹{ms.balanceDue.toLocaleString("en-IN")} — please
            contact the gym staff.
          </div>
        )}

        {/* Payment history toggle */}
        {ms.payments?.length > 0 && (
          <div>
            <button
              onClick={() => setOpen(!open)}
              className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
            >
              <Receipt className="h-3.5 w-3.5" />
              {open ? "Hide" : "Show"} {ms.payments.length} payment
              {ms.payments.length > 1 ? "s" : ""}
              {open ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </button>
            {open && (
              <div className="mt-3 space-y-0 border border-border rounded-lg overflow-hidden">
                {ms.payments.map((p, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between px-3 py-2.5 border-b border-border last:border-0 bg-secondary/30"
                  >
                    <div>
                      <p className="text-sm font-medium">
                        ₹{(p.amount || 0).toLocaleString("en-IN")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {p.paymentMode || "Cash"}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {p.date
                        ? new Date(p.date).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const getEffectiveStatus = (status, endDate) => {
  if (!["Active", "Partial"].includes(status)) {
    return status;
  }
  if (!endDate) return status;

  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  const now = new Date();
  if (now > end) {
    return "Expired";
  }
  return status;
};

export default function MemberMembership() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("member_token");
      if (!token) {
        navigate("/");
        return;
      }
      try {
        const res = await api.get("/member-portal/memberships", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const membershipData = res.data;
        if (membershipData.memberships) {
          membershipData.memberships = membershipData.memberships.map((ms) => ({
            ...ms,
            status: getEffectiveStatus(ms.status, ms.endDate),
          }));
        }
        setData(membershipData);
      } catch (err) {
        if (err?.response?.status === 401) {
          navigate("/");
          return;
        }
        setError("Failed to load membership details.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );

  if (error)
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-muted-foreground">
        <AlertCircle className="h-8 w-8" />
        <p>{error}</p>
      </div>
    );

  const active =
    data?.memberships?.filter((m) =>
      ["Active", "Partial"].includes(m.status),
    ) || [];
  const others =
    data?.memberships?.filter(
      (m) => !["Active", "Partial"].includes(m.status),
    ) || [];

  const noMembership = active.length === 0 && others.length === 0;

  return (
    <div className="space-y-6 pb-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-black font-heading uppercase tracking-wide">
          Membership
        </h1>
        <p className="text-muted-foreground text-xs mt-0.5">
          Your membership plans and payment history
        </p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold sm:text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5 truncate">
                  Status
                </p>
                <p
                  className={`text-base sm:text-xl font-black font-heading line-clamp-2 break-words ${
                    active.length > 0 ? "text-green-500" : "text-red-500"
                  }`}
                >
                  {active.length > 0 ? "Active" : "None"}
                </p>
              </div>
              <div className="w-10 h-10  flex items-center justify-center text-primary">
                <CreditCard className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold sm:text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5 truncate">
                  Total Plans
                </p>
                <p className="text-base sm:text-xl font-black font-heading line-clamp-2 break-words text-primary text-primary">
                  {data?.memberships?.length || 0}
                </p>
              </div>
              <div className="w-10 h-10  flex items-center justify-center text-primary">
                <CalendarDays className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {noMembership && (
        <Card className="bg-card border-border mt-6">
          <CardContent className="flex flex-col items-center justify-center py-14 gap-3 text-muted-foreground">
            <CreditCard className="h-10 w-10 opacity-30" />
            <p className="text-sm">No membership plans found</p>
            <p className="text-xs text-center max-w-xs">
              Contact the gym staff to get enrolled in a membership plan.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Active memberships */}
      {active.length > 0 && (
        <div className="space-y-4 mt-6">
          <h2 className="text-xs font-semibold sm:text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5 truncate">
            Active Plan
          </h2>
          {active.map((ms, i) => (
            <MembershipCard key={i} ms={ms} />
          ))}
        </div>
      )}

      {/* Past memberships */}
      {others.length > 0 && (
        <div className="space-y-4 mt-6">
          <h2 className="text-xs font-semibold sm:text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5 truncate">
            Past Plans
          </h2>
          {others.map((ms, i) => (
            <MembershipCard key={i} ms={ms} />
          ))}
        </div>
      )}
    </div>
  );
}
